import { useCallback, useEffect, useState } from 'react';
import type { components } from '@/generated/contracts/api-v1';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { BE_TO_FE_CHANNEL } from '@/modules/sceneHealth/feChannelConfig';
import type { ChannelStatus, ChannelVerdict, Reason, ReasonType } from '@/modules/sceneHealth';

type BackendChannelReason = components['schemas']['SceneHealthReasonDto'];
type BackendChannelVerdict = components['schemas']['SceneHealthChannelDto'];

function mapReason(raw: BackendChannelReason): Reason {
  const type = raw.type as ReasonType;
  if (type === 'feature_blocked') {
    return { type, feature: raw.feature ?? undefined };
  }
  return { type, thresholdMB: raw.thresholdMb ?? undefined };
}

function mapVerdict(raw: BackendChannelVerdict): ChannelVerdict | null {
  const key = BE_TO_FE_CHANNEL[raw.key];
  if (!key) return null;
  return {
    key,
    status: raw.status as ChannelStatus,
    reasons: raw.reasons.map(mapReason),
  };
}

export interface SceneHealthData {
  totalMB: number;
  featuresUsed: string[];
  verdicts: ChannelVerdict[];
}

const cache = new Map<string, SceneHealthData>();
const inflight = new Map<string, Promise<SceneHealthData>>();
// Scenes whose last check failed, until they are checked again
const failures = new Set<string>();
const subscribers = new Map<string, Set<() => void>>();

function notify(id: string) {
  subscribers.get(id)?.forEach((cb) => cb());
}

async function fetchHealth(sceneInstanceId: string): Promise<SceneHealthData> {
  const existing = inflight.get(sceneInstanceId);
  if (existing) return existing;
  const p = (async () => {
    if (failures.delete(sceneInstanceId)) notify(sceneInstanceId);
    try {
      const { data: json, error } = await toResult(v1.GET('/api/v1/scenes/{sceneUniqueId}/health', { params: { path: { sceneUniqueId: sceneInstanceId } } }));
      if (error) throw new Error(`Scene health fetch failed: ${error.status} ${error.code}`);
      const verdicts = json.channels
        .map(mapVerdict)
        .filter((v): v is ChannelVerdict => v !== null);
      const totalMB = Number.isFinite(json.totalMegabytes) ? json.totalMegabytes : 0;
      const next = {
        totalMB: Math.round(totalMB * 100) / 100,
        featuresUsed: json.featuresUsed ?? [],
        verdicts,
      };
      cache.set(sceneInstanceId, next);
      return next;
    } catch (error) {
      // Every view of the scene's health shows that it failed, and can ask again
      failures.add(sceneInstanceId);
      throw error;
    } finally {
      // Let go however it settled, so a failed check is asked again by the next fetch instead of handed back forever
      inflight.delete(sceneInstanceId);
      notify(sceneInstanceId);
    }
  })();
  inflight.set(sceneInstanceId, p);
  return p;
}

export function useSceneHealth(sceneInstanceId: string | null | undefined): {
  data: SceneHealthData | null;
  loading: boolean;
  /** The last check failed; refetch asks again. */
  failed: boolean;
  refetch: () => void;
} {
  const [, forceUpdate] = useState(0);

  const data = sceneInstanceId ? cache.get(sceneInstanceId) ?? null : null;
  const failed = !!sceneInstanceId && failures.has(sceneInstanceId);
  const loading = !!sceneInstanceId && !cache.has(sceneInstanceId) && !failed;

  useEffect(() => {
    if (!sceneInstanceId) return;

    const subSet = subscribers.get(sceneInstanceId) ?? new Set<() => void>();
    const onChange = () => forceUpdate((n) => n + 1);
    subSet.add(onChange);
    subscribers.set(sceneInstanceId, subSet);

    if (!cache.has(sceneInstanceId)) {
      fetchHealth(sceneInstanceId).catch((err) => console.error('useSceneHealth', err));
    }

    return () => {
      subSet.delete(onChange);
      if (subSet.size === 0) subscribers.delete(sceneInstanceId);
    };
  }, [sceneInstanceId]);

  const refetch = useCallback(() => {
    if (!sceneInstanceId) return;
    cache.delete(sceneInstanceId);
    forceUpdate((n) => n + 1);
    fetchHealth(sceneInstanceId).catch((err) => console.error('useSceneHealth', err));
  }, [sceneInstanceId]);

  return { data, loading, failed, refetch };
}
